import type { Tables } from '../../types/database.types';

// CIME roles (mirror collaborators.role column in DB)
export type CollaboratorRole = 'admin' | 'lawyer' | 'secretary' | 'client_portal';

/**
 * Human-readable labels for each role.
 * Single source of truth — import this instead of hardcoding strings.
 * NOTE: DB slug for "Assistant(e)" is `secretary` — do NOT change slug.
 */
export const ROLE_LABELS: Record<CollaboratorRole, string> = {
  admin: 'Administrateur',
  lawyer: 'Avocat',
  secretary: 'Assistant(e)',
  client_portal: 'Client',
};

/**
 * Feature → allowed roles map.
 *
 * ⚠️ CHECKLIST: whenever you add a new feature/page, add an entry here.
 *   1. Pick a snake_case key that describes the feature
 *   2. Add the minimal set of roles that should access it
 *   3. Reference it via `feature` in navItems.ts or <PermissionGuard feature="..." />
 */
export const RESTRICTED_FEATURES = {
  // ─── ADMIN ONLY ──────────────────────────────────────────────────────────
  design_system: ['admin'],
  logs_view: ['admin', 'secretary'],
  settings_firm: ['admin'],
  settings_branding: ['admin'],
  settings_team: ['admin'],
  settings_permissions: ['admin'],
  settings_categories: ['admin'],
  settings_tags: ['admin'],
  settings_doc_templates: ['admin'],
  settings_task_templates: ['admin'],
  settings_dossiers: ['admin'],
  settings_agenda: ['admin'], // onglet Paramètres › Agenda (types d'audience et de rendez-vous)
  settings_ai_agent: ['admin'],
  billing_management: ['admin'], // global cabinet billing management
  user_impersonation: ['admin'], // future

  // ─── ADMIN + LAWYER ──────────────────────────────────────────────────────
  outlook_connect: ['admin', 'lawyer'], // connecter son propre compte Outlook
  outlook_read:    ['admin', 'lawyer'], // lire les emails rattachés à un dossier
  outlook_send:    ['admin', 'lawyer'], // envoyer un email depuis l'app
  dossiers_create: ['admin', 'lawyer', 'secretary'],
  dossiers_delete: ['admin'],
  dossiers_edit: ['admin', 'lawyer'],
  invoices_create: ['admin', 'lawyer', 'secretary'],
  invoices_send: ['admin', 'lawyer', 'secretary'],
  time_tracking_billable: ['admin', 'lawyer'],
  ai_legal_assistant: ['admin', 'lawyer'],
  client_portal_invite: ['admin', 'lawyer'],
  signature_circuit_create: ['admin', 'lawyer', 'secretary'],
  // Répondre par email à un candidat (demande de recrutement).
  // Garde supplémentaire côté UI ET serveur : il faut aussi être admin, ou
  // l'assigné, ou le référent de la demande (cf. canReplyToCandidate).
  tickets_reply: ['admin', 'lawyer'],

  // ─── ADMIN + LAWYER + ASSISTANT(E) ───────────────────────────────────────
  tickets_view: ['admin', 'lawyer', 'secretary'],
  profile_view: ['admin', 'lawyer', 'secretary'], // consulter une fiche profil publique
  tickets_assign: ['admin', 'secretary'], // lawyers receive tickets, they don't assign them
  tickets_claim: ['admin', 'lawyer', 'secretary'], // "prendre en charge" un ticket qui m'est assigné
  tickets_respond: ['admin', 'lawyer', 'secretary'],
  tickets_close: ['admin', 'lawyer', 'secretary'],
  tickets_delete: ['admin', 'secretary'],
  tickets_archive: ['admin', 'secretary'],
  tickets_edit_category: ['admin', 'lawyer', 'secretary'],
  tickets_attach: ['admin', 'lawyer', 'secretary'],
  tickets_delegate: ['admin', 'lawyer'],
  dossiers_view: ['admin', 'lawyer', 'secretary'],
  dossiers_assign_members: ['admin', 'lawyer'],
  dossiers_view_all: ['admin'],
  contacts_view: ['admin', 'lawyer', 'secretary'],
  contacts_edit: ['admin', 'lawyer', 'secretary'],
  contacts_delete: ['admin'],
  contacts_import: ['admin'],
  tasks_view: ['admin', 'lawyer', 'secretary'],
  tasks_create: ['admin', 'lawyer', 'secretary'],
  tasks_nudge: ['admin', 'lawyer', 'secretary'], // bouton « Relancer » : notification + mail
  tasks_edit: ['admin', 'lawyer', 'secretary'],
  tasks_assign: ['admin', 'lawyer'],
  tasks_delete: ['admin', 'lawyer'],
  agenda_view: ['admin', 'lawyer', 'secretary'],
  agenda_edit: ['admin', 'lawyer', 'secretary'],
  hearings_create: ['admin', 'lawyer', 'secretary'],
  appointments_manage: ['admin', 'lawyer', 'secretary'],
  messaging: ['admin', 'lawyer', 'secretary'],
  signature_my_parapheur: ['admin', 'lawyer', 'secretary'],
  messaging_send: ['admin', 'lawyer', 'secretary'],
  messaging_delete_own: ['admin', 'lawyer', 'secretary'],
  // Personne ne peut supprimer le message d'un autre (y compris admin).
  messaging_delete_any: [],
  messaging_create_channel: ['admin'],
  messaging_manage_channel: ['admin'],
  dossier_channel_invite: ['admin', 'lawyer'],
  files_view: ['admin', 'lawyer', 'secretary'],
  files_edit: ['admin', 'lawyer', 'secretary'],
  // Bibliothèque cabinet (modèles, jurisprudences, RH, etc.) — accès filtré par
  // cabinet_folder_members côté DB ; ces flags ne servent qu'à gating UI globale.
  cabinet_library: ['admin', 'lawyer', 'secretary'],
  cabinet_library_manage_roots: ['admin'],
  cabinet_library_manage_tags: ['admin'],
  time_entries_view: ['admin', 'lawyer', 'secretary'],
  time_entries_create: ['admin', 'lawyer', 'secretary'],
  time_entries_edit_own: ['admin', 'lawyer'],
  time_entries_delete_own: ['admin', 'lawyer'],
  hourly_rate_edit: ['admin'],
  billing_view: ['admin', 'lawyer', 'secretary'],
  billing_create: ['admin', 'lawyer', 'secretary'],
  billing_issue: ['admin', 'lawyer', 'secretary'],
  billing_mark_paid: ['admin', 'lawyer', 'secretary'],
  billing_void: ['admin', 'lawyer', 'secretary'],
  // Frais et débours avancés pour un dossier : saisie, justificatif, refacturation.
  expenses_create: ['admin', 'lawyer', 'secretary'],
  // Mandat de débours du dossier (date et référence exigées par Polaris).
  debours_mandate_edit: ['admin', 'lawyer', 'secretary'],
  fee_agreement_view: ['admin', 'lawyer'],
  fee_agreement_edit: ['admin', 'lawyer'],
  fee_agreement_ai_create: ['admin', 'lawyer'],
  timesheet_view: ['admin', 'lawyer', 'secretary'],
  timer_use: ['admin', 'lawyer', 'secretary'],
  stats_cabinet_view: ['admin'],
  calendar_view: ['admin', 'lawyer', 'secretary'],
  calendar_google_sync: ['admin', 'lawyer', 'secretary'],
  gmail_inbox_view: ['admin', 'lawyer'],
  ebarreau_view: ['admin', 'lawyer'],
  email_graft_triage: ['admin', 'lawyer', 'secretary'], // widget Dashboard « Mails à rattacher »
  documents_generate: ['admin', 'lawyer', 'secretary'], // assistant de génération de documents IA
  outlook_addin: ['admin', 'lawyer', 'secretary'], // panneau add-in Outlook (greffe/archivage) — garde EF côté serveur

  search_view: ['admin', 'lawyer', 'secretary'], // page de résultats de la recherche universelle (chaque famille garde son propre droit)

  // ─── ALL (including future client_portal) ────────────────────────────────
  portal_access: ['client_portal'],
  dashboard: ['admin', 'lawyer', 'secretary', 'client_portal'],
  profile_settings: ['admin', 'lawyer', 'secretary', 'client_portal'],
  notifications_view: ['admin', 'lawyer', 'secretary', 'client_portal'],
  notifications_manage: ['admin', 'lawyer', 'secretary', 'client_portal'],
} as const;

export type Feature = keyof typeof RESTRICTED_FEATURES;

/**
 * Synchronous check: can a given role access a feature?
 *
 * @example
 *   canAccessFeature('admin', 'invoices_create') // true
 *   canAccessFeature('secretary', 'settings_firm') // false
 */
export function canAccessFeature(
  role: CollaboratorRole | null | undefined,
  feature: Feature,
): boolean {
  if (!role) return false;
  const allowedRoles = RESTRICTED_FEATURES[feature] as readonly string[];
  return allowedRoles.includes(role);
}

/**
 * Returns true if the role is `admin`.
 */
export function isAdmin(role: CollaboratorRole | null | undefined): boolean {
  return role === 'admin';
}

/**
 * Returns true if the role is `admin` or `secretary` (ticket management logic).
 */
export function isAdminOrSecretary(role: CollaboratorRole | null | undefined): boolean {
  return role === 'admin' || role === 'secretary';
}

/**
 * Narrows the raw DB string into our typed `CollaboratorRole`.
 * Returns null for unknown/unrecognised values.
 */
export function asCollaboratorRole(
  role: Tables<'collaborators'>['role'] | null | undefined,
): CollaboratorRole | null {
  if (
    role === 'admin' ||
    role === 'lawyer' ||
    role === 'secretary' ||
    role === 'client_portal'
  ) {
    return role;
  }
  return null;
}
